import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Activity,
  Search,
  Download,
  User,
  Shield,
  Clock,
  Eye,
  X,
  RefreshCw,
  FileText,
  LogIn,
  CheckCircle2,
  AlertTriangle,
  ShieldAlert,
  Globe,
  Laptop,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { SkeletonTimeline, SkeletonTable } from '@/components/ui/skeleton.js';
import { Pagination } from '@/components/ui/Pagination.js';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { useToast } from '@/context/ToastContext.js';

export interface ActivityLogItem {
  id: string;
  actor_id: string;
  actor_name: string;
  actor_avatar?: string | null;
  action: string;
  entity_type: string;
  entity_id?: string | null;
  entity_name?: string | null;
  description: string;
  metadata?: Record<string, any> | null;
  ip_address?: string | null;
  created_at: string;
}

export interface LoginActivityItem {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  user_role: string;
  status: 'success' | 'failed' | 'otp_challenged';
  ip_address: string;
  user_agent: string;
  failure_reason?: string | null;
  created_at: string;
}

export function ActivityLogsPage() {
  const { user } = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const isPrivileged = user?.role === 'owner' || user?.role === 'manager';
  const [activeTab, setActiveTab] = useState<'audit' | 'login_activity'>(
    tabParam === 'login_activity' ? 'login_activity' : 'audit'
  );

  useEffect(() => {
    if (tabParam === 'login_activity') {
      setActiveTab('login_activity');
    } else if (tabParam === 'audit') {
      setActiveTab('audit');
    }
  }, [tabParam]);

  const [logs, setLogs] = useState<ActivityLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [actorFilter, setActorFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'timeline' | 'table'>('timeline');
  const [selectedLog, setSelectedLog] = useState<ActivityLogItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  // Login Activity States
  const [loginActivities, setLoginActivities] = useState<LoginActivityItem[]>([]);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginSearch, setLoginSearch] = useState('');
  const [loginStatusFilter, setLoginStatusFilter] = useState('all');
  const [loginPage, setLoginPage] = useState(1);
  const loginPageSize = 15;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get<{ data: ActivityLogItem[] }>('/api/v1/activity');
      const list = res?.data || (Array.isArray(res) ? res : []);
      setLogs(list);
    } catch (err: any) {
      console.error('Failed to load activity logs', err);
      toast.error(err?.message || 'Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchLoginActivities = async () => {
    if (!isPrivileged) return;
    setLoginLoading(true);
    try {
      const res = await api.get<{ data: LoginActivityItem[] }>('/api/v1/login-activities');
      const list = res?.data || (Array.isArray(res) ? res : []);
      setLoginActivities(list);
    } catch (err: any) {
      console.error('Failed to load login activities', err);
      toast.error(err?.message || 'Failed to fetch login activities');
    } finally {
      setLoginLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    if (isPrivileged) {
      fetchLoginActivities();
    }
  }, [isPrivileged]);

  // Unique actors for filter dropdown
  const uniqueActors = useMemo(() => {
    const map = new Map<string, string>();
    logs.forEach((log) => {
      if (log.actor_id && log.actor_name) {
        map.set(log.actor_id, log.actor_name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [logs]);

  // Filtered logs
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (entityFilter !== 'all' && log.entity_type !== entityFilter) {
        return false;
      }
      if (actorFilter !== 'all' && log.actor_id !== actorFilter) {
        return false;
      }
      if (search.trim()) {
        const q = search.toLowerCase();
        const matchesDesc = (log.description || '').toLowerCase().includes(q);
        const matchesActor = (log.actor_name || '').toLowerCase().includes(q);
        const matchesAction = (log.action || '').toLowerCase().includes(q);
        const matchesEntity = (log.entity_name || '').toLowerCase().includes(q);
        if (!matchesDesc && !matchesActor && !matchesAction && !matchesEntity) {
          return false;
        }
      }
      return true;
    });
  }, [logs, entityFilter, actorFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / pageSize));
  const validCurrentPage = Math.min(currentPage, totalPages);

  const paginatedLogs = useMemo(() => {
    const start = (validCurrentPage - 1) * pageSize;
    return filteredLogs.slice(start, start + pageSize);
  }, [filteredLogs, validCurrentPage, pageSize]);

  // KPI Calculations
  const stats = useMemo(() => {
    const total = logs.length;
    const today = new Date().toISOString().slice(0, 10);
    const todayCount = logs.filter((l) => (l.created_at || '').startsWith(today)).length;
    const sensitiveCount = logs.filter((l) =>
      ['delete', 'demote', 'deactivate', 'export', 'api_key', 'mcp_override'].some((kw) =>
        (l.action || '').toLowerCase().includes(kw)
      )
    ).length;
    const uniqueContributors = new Set(logs.map((l) => l.actor_id)).size;

    return { total, todayCount, sensitiveCount, uniqueContributors };
  }, [logs]);

  // Formula-safe CSV export with UTF-8 BOM and Blob download
  const handleExportCsv = () => {
    try {
      const headers = ['Timestamp (UTC)', 'Actor Name', 'Action', 'Entity Type', 'Entity Name', 'Description'];
      const rows = filteredLogs.map((l) => [
        l.created_at,
        l.actor_name,
        l.action,
        l.entity_type,
        l.entity_name || '',
        l.description,
      ]);

      const sanitize = (val: any) => {
        let clean = String(val ?? '');
        // Defense against spreadsheet formula injection (=, +, -, @, \t, \r, |, %)
        if (/^[=+\-@\t\r|%]/.test(clean)) {
          clean = `'${clean}`;
        }
        return `"${clean.replace(/"/g, '""')}"`;
      };

      const csvRows = [
        headers.map(sanitize).join(','),
        ...rows.map((r) => r.map(sanitize).join(','))
      ];
      // UTF-8 BOM (\uFEFF) ensures Excel opens multilingual characters correctly
      const blob = new Blob(['\uFEFF' + csvRows.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `techosolution_activity_audit_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      toast.success('Activity audit log exported to CSV successfully');
    } catch {
      toast.error('Failed to export activity log');
    }
  };

  // Login Activity Calculations & Filtered Data
  const loginStats = useMemo(() => {
    const total = loginActivities.length;
    const successCount = loginActivities.filter((a) => a.status === 'success').length;
    const otpCount = loginActivities.filter((a) => a.status === 'otp_challenged').length;
    const failedCount = loginActivities.filter((a) => a.status === 'failed').length;
    return { total, successCount, otpCount, failedCount };
  }, [loginActivities]);

  const filteredLoginActivities = useMemo(() => {
    return loginActivities.filter((item) => {
      if (loginStatusFilter !== 'all' && item.status !== loginStatusFilter) {
        return false;
      }
      if (loginSearch.trim()) {
        const q = loginSearch.toLowerCase();
        const matchesName = (item.user_name || '').toLowerCase().includes(q);
        const matchesEmail = (item.user_email || '').toLowerCase().includes(q);
        const matchesIp = (item.ip_address || '').toLowerCase().includes(q);
        const matchesAgent = (item.user_agent || '').toLowerCase().includes(q);
        const matchesReason = (item.failure_reason || '').toLowerCase().includes(q);
        if (!matchesName && !matchesEmail && !matchesIp && !matchesAgent && !matchesReason) {
          return false;
        }
      }
      return true;
    });
  }, [loginActivities, loginStatusFilter, loginSearch]);

  const totalLoginPages = Math.max(1, Math.ceil(filteredLoginActivities.length / loginPageSize));
  const validCurrentLoginPage = Math.min(loginPage, totalLoginPages);

  const paginatedLoginActivities = useMemo(() => {
    const start = (validCurrentLoginPage - 1) * loginPageSize;
    return filteredLoginActivities.slice(start, start + loginPageSize);
  }, [filteredLoginActivities, validCurrentLoginPage, loginPageSize]);

  const handleExportLoginCsv = () => {
    try {
      const headers = ['Timestamp (PKT)', 'Member Name', 'Member Email', 'Role', 'Status', 'IP Address', 'Device / Browser', 'Failure Reason'];
      const rows = filteredLoginActivities.map((l) => [
        new Date(l.created_at).toLocaleString('en-US', { timeZone: 'Asia/Karachi' }),
        l.user_name,
        l.user_email,
        l.user_role,
        l.status,
        l.ip_address,
        l.user_agent,
        l.failure_reason || '',
      ]);

      const sanitize = (val: any) => {
        let clean = String(val ?? '');
        if (/^[=+\-@\t\r|%]/.test(clean)) {
          clean = `'${clean}`;
        }
        return `"${clean.replace(/"/g, '""')}"`;
      };

      const csvRows = [
        headers.map(sanitize).join(','),
        ...rows.map((r) => r.map(sanitize).join(','))
      ];
      const blob = new Blob(['\uFEFF' + csvRows.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `techosolution_login_activity_audit_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      toast.success('Login activity exported to CSV successfully');
    } catch {
      toast.error('Failed to export login activity');
    }
  };

  const formatRelativeTime = (isoString?: string | null) => {
    if (!isoString) return 'Never';
    const diffMs = Date.now() - new Date(isoString).getTime();
    if (diffMs < 0 || isNaN(diffMs)) return 'Just now';
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'Asia/Karachi' });
  };

  const getActionBadge = (action: string) => {
    const act = (action || '').toLowerCase();
    if (act.includes('create') || act.includes('add') || act.includes('convert')) {
      return <Badge variant="success">{action}</Badge>;
    }
    if (act.includes('delete') || act.includes('remove') || act.includes('deactivate')) {
      return <Badge variant="destructive">{action}</Badge>;
    }
    if (act.includes('update') || act.includes('edit') || act.includes('override')) {
      return <Badge variant="outline">{action}</Badge>;
    }
    if (act.includes('complete') || act.includes('approve')) {
      return <Badge variant="default">{action}</Badge>;
    }
    return <Badge variant="secondary">{action}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <h1 className="text-2xl font-bold tracking-tight text-brand-ink">Activity & Audit Logs</h1>
            <Badge variant="outline" className="hidden sm:inline-flex">
              Compliance Trail
            </Badge>
          </div>
          <p className="text-xs sm:text-sm text-brand-muted mt-0.5">
            {activeTab === 'audit'
              ? 'Full immutable workspace audit stream tracking task transitions, role changes, and security events.'
              : 'Real-time security audit trail tracking member login sessions, 2FA challenges, IP addresses, and device fingerprints.'}
          </p>
        </div>
        <div className="flex items-center space-x-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => (activeTab === 'audit' ? fetchLogs() : fetchLoginActivities())}
            disabled={activeTab === 'audit' ? loading : loginLoading}
            className="flex items-center space-x-1 text-xs"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${((activeTab === 'audit' && loading) || (activeTab === 'login_activity' && loginLoading)) ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={activeTab === 'audit' ? handleExportCsv : handleExportLoginCsv}
            disabled={activeTab === 'audit' ? filteredLogs.length === 0 : filteredLoginActivities.length === 0}
            className="flex items-center space-x-1.5 text-xs font-semibold shadow-sm"
          >
            <Download className="h-3.5 w-3.5" />
            <span>Export CSV</span>
          </Button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center space-x-2 border-b border-brand-border pb-2">
        <button
          onClick={() => {
            setActiveTab('audit');
            setSearchParams({});
          }}
          className={`flex items-center space-x-2 px-3.5 py-2 rounded-xl text-xs font-bold transition-all ${
            activeTab === 'audit'
              ? 'bg-brand text-white shadow-xs'
              : 'text-brand-muted hover:text-brand-ink hover:bg-brand-tint/30'
          }`}
        >
          <Activity className="h-4 w-4" />
          <span>Workspace Audit Trail</span>
          <Badge variant={activeTab === 'audit' ? 'outline' : 'secondary'} className="ml-1 text-[10px]">
            {logs.length}
          </Badge>
        </button>

        <button
          onClick={() => {
            setActiveTab('login_activity');
            setSearchParams({ tab: 'login_activity' });
            if (isPrivileged && loginActivities.length === 0) {
              fetchLoginActivities();
            }
          }}
          className={`flex items-center space-x-2 px-3.5 py-2 rounded-xl text-xs font-bold transition-all ${
            activeTab === 'login_activity'
              ? 'bg-brand text-white shadow-xs'
              : 'text-brand-muted hover:text-brand-ink hover:bg-brand-tint/30'
          }`}
        >
          <LogIn className="h-4 w-4" />
          <span>Member Login Activity</span>
          {loginActivities.length > 0 && (
            <Badge variant={activeTab === 'login_activity' ? 'outline' : 'secondary'} className="ml-1 text-[10px]">
              {loginActivities.length}
            </Badge>
          )}
        </button>
      </div>

      {activeTab === 'login_activity' ? (
        !isPrivileged ? (
          <Card className="text-center p-12 space-y-3 border-amber-200 dark:border-amber-900/50 bg-amber-50/30 dark:bg-amber-950/20">
            <ShieldAlert className="h-10 w-10 text-amber-500 mx-auto" />
            <h3 className="text-base font-bold text-brand-ink">Confidential Security Audit</h3>
            <p className="text-xs text-brand-muted max-w-md mx-auto">
              The member login activity stream contains authentication records, IP addresses, and device fingerprints. Access is restricted to Agency Owners and Managers.
            </p>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Login KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="hover:shadow-hover transition-all">
                <CardContent className="p-4 sm:p-5 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-brand-muted">Total Login Events</p>
                    <h3 className="text-xl sm:text-2xl font-black text-brand-ink mt-1">{loginStats.total}</h3>
                    <p className="text-[10px] text-brand-muted mt-0.5">Tracked sessions</p>
                  </div>
                  <div className="p-2.5 rounded-xl bg-brand-tint/60 text-brand">
                    <LogIn className="h-5 w-5" />
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-hover transition-all">
                <CardContent className="p-4 sm:p-5 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-brand-muted">Successful Logins</p>
                    <h3 className="text-xl sm:text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
                      {loginStats.successCount}
                    </h3>
                    <p className="text-[10px] text-brand-muted mt-0.5">Authenticated sessions</p>
                  </div>
                  <div className="p-2.5 rounded-xl bg-emerald-100/60 dark:bg-emerald-950/40 text-emerald-600">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-hover transition-all">
                <CardContent className="p-4 sm:p-5 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-brand-muted">2FA OTP Challenges</p>
                    <h3 className="text-xl sm:text-2xl font-black text-teal-600 dark:text-teal-400 mt-1">
                      {loginStats.otpCount}
                    </h3>
                    <p className="text-[10px] text-brand-muted mt-0.5">Owner 2FA checkpoints</p>
                  </div>
                  <div className="p-2.5 rounded-xl bg-teal-100/60 dark:bg-teal-950/40 text-teal-600">
                    <Shield className="h-5 w-5" />
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-hover transition-all">
                <CardContent className="p-4 sm:p-5 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-brand-muted">Failed Attempts</p>
                    <h3 className="text-xl sm:text-2xl font-black text-rose-600 dark:text-rose-400 mt-1">
                      {loginStats.failedCount}
                    </h3>
                    <p className="text-[10px] text-brand-muted mt-0.5">Rejected credentials/OTP</p>
                  </div>
                  <div className="p-2.5 rounded-xl bg-rose-100/60 dark:bg-rose-950/40 text-rose-600">
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Login Filter Toolbar */}
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 bg-surface p-3.5 rounded-xl border border-brand-border shadow-card">
              <div className="flex flex-1 flex-wrap items-center gap-2.5">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-brand-muted" />
                  <Input
                    type="text"
                    placeholder="Search by name, email, IP, device, or reason..."
                    value={loginSearch}
                    onChange={(e) => {
                      setLoginSearch(e.target.value);
                      setLoginPage(1);
                    }}
                    className="pl-9 h-9 text-xs"
                  />
                </div>

                <select
                  value={loginStatusFilter}
                  onChange={(e) => {
                    setLoginStatusFilter(e.target.value);
                    setLoginPage(1);
                  }}
                  className="h-9 rounded-input border border-brand-border bg-surface px-3 text-xs text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand/30"
                >
                  <option value="all">All Statuses</option>
                  <option value="success">Successful Logins</option>
                  <option value="otp_challenged">2FA OTP Challenged</option>
                  <option value="failed">Failed Attempts</option>
                </select>
              </div>
            </div>

            {/* Login Activities Table */}
            {loginLoading ? (
              <SkeletonTable rows={8} cols={5} />
            ) : filteredLoginActivities.length === 0 ? (
              <Card className="text-center p-12 space-y-3">
                <LogIn className="h-8 w-8 text-brand-muted mx-auto" />
                <h3 className="text-sm font-bold text-brand-ink">No login activity records found</h3>
                <p className="text-xs text-brand-muted max-w-sm mx-auto">
                  {loginSearch || loginStatusFilter !== 'all'
                    ? 'Try adjusting your filters or search terms.'
                    : 'As team members log into the agency portal, authentication records will appear here in real time.'}
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="rounded-xl border border-brand-border bg-surface overflow-hidden shadow-card">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="border-b border-brand-border bg-slate-50/70 dark:bg-[#0E1A18] text-brand-muted font-bold uppercase tracking-wider text-[10px]">
                          <th className="py-3 px-4">Timestamp (PKT)</th>
                          <th className="py-3 px-4">Member</th>
                          <th className="py-3 px-4">Status</th>
                          <th className="py-3 px-4">IP Address</th>
                          <th className="py-3 px-4">Device / Client</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-brand-border/50 text-brand-ink">
                        {paginatedLoginActivities.map((item) => {
                          const formattedTime = new Date(item.created_at).toLocaleString('en-US', {
                            timeZone: 'Asia/Karachi',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          });

                          return (
                            <tr key={item.id} className="hover:bg-slate-50/50 dark:hover:bg-[#162C29]/30 transition-colors">
                              <td className="py-3 px-4 whitespace-nowrap">
                                <div className="font-mono text-[11px] text-brand-muted">{formattedTime}</div>
                                <div className="text-[10px] text-brand-muted/70 flex items-center gap-1 mt-0.5">
                                  <Clock className="h-2.5 w-2.5" />
                                  {formatRelativeTime(item.created_at)}
                                </div>
                              </td>

                              <td className="py-3 px-4 whitespace-nowrap">
                                <div className="flex items-center space-x-2">
                                  <div className="h-7 w-7 rounded-full bg-brand-tint flex items-center justify-center text-brand-ink font-bold text-[11px] shrink-0">
                                    {item.user_name ? item.user_name.charAt(0).toUpperCase() : (item.user_email ? item.user_email.charAt(0).toUpperCase() : '?')}
                                  </div>
                                  <div>
                                    <div className="font-semibold text-brand-ink flex items-center gap-1.5">
                                      <span>{item.user_name || 'Anonymous User'}</span>
                                      {item.user_role && (
                                        <span className="text-[10px] font-mono px-1.5 py-0.2 rounded uppercase bg-brand-surface border border-brand-border text-brand-muted">
                                          {item.user_role}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-[11px] text-brand-muted">{item.user_email}</div>
                                  </div>
                                </div>
                              </td>

                              <td className="py-3 px-4 whitespace-nowrap">
                                {item.status === 'success' && (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Success
                                  </span>
                                )}
                                {item.status === 'otp_challenged' && (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-400">
                                    <Shield className="h-3 w-3" />
                                    2FA Challenged
                                  </span>
                                )}
                                {item.status === 'failed' && (
                                  <div className="flex flex-col">
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-400 w-fit">
                                      <AlertTriangle className="h-3 w-3" />
                                      Failed
                                    </span>
                                    {item.failure_reason && (
                                      <span className="text-[10px] text-rose-500 font-mono mt-0.5 max-w-[200px] truncate" title={item.failure_reason}>
                                        {item.failure_reason}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>

                              <td className="py-3 px-4 whitespace-nowrap font-mono text-[11px] text-brand-muted">
                                <div className="flex items-center gap-1.5">
                                  <Globe className="h-3.5 w-3.5 text-brand-muted/70 shrink-0" />
                                  <span>{item.ip_address || '—'}</span>
                                </div>
                              </td>

                              <td className="py-3 px-4 text-brand-muted text-[11px] max-w-xs truncate" title={item.user_agent}>
                                <div className="flex items-center gap-1.5">
                                  <Laptop className="h-3.5 w-3.5 text-brand-muted/70 shrink-0" />
                                  <span className="truncate">{item.user_agent || 'Unknown Device'}</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <Pagination
                  currentPage={validCurrentLoginPage}
                  totalItems={filteredLoginActivities.length}
                  pageSize={loginPageSize}
                  onPageChange={setLoginPage}
                />
              </div>
            )}
          </div>
        )
      ) : (
        <>
          {/* KPI Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="hover:shadow-hover transition-all">
              <CardContent className="p-4 sm:p-5 flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-brand-muted">Total Events</p>
                  <h3 className="text-xl sm:text-2xl font-black text-brand-ink mt-1">{stats.total}</h3>
                  <p className="text-[10px] text-brand-muted mt-0.5">Recorded in workspace</p>
                </div>
                <div className="p-2.5 rounded-xl bg-brand-tint/60 text-brand">
                  <Activity className="h-5 w-5" />
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-hover transition-all">
              <CardContent className="p-4 sm:p-5 flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-brand-muted">Today's Activity</p>
                  <h3 className="text-xl sm:text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
                    {stats.todayCount}
                  </h3>
                  <p className="text-[10px] text-brand-muted mt-0.5">Events in last 24h</p>
                </div>
                <div className="p-2.5 rounded-xl bg-emerald-100/60 dark:bg-emerald-950/40 text-emerald-600">
                  <Clock className="h-5 w-5" />
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-hover transition-all">
              <CardContent className="p-4 sm:p-5 flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-brand-muted">Sensitive Operations</p>
                  <h3 className="text-xl sm:text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">
                    {stats.sensitiveCount}
                  </h3>
                  <p className="text-[10px] text-brand-muted mt-0.5">Deletions & role edits</p>
                </div>
                <div className="p-2.5 rounded-xl bg-amber-100/60 dark:bg-amber-950/40 text-amber-600">
                  <Shield className="h-5 w-5" />
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-hover transition-all">
              <CardContent className="p-4 sm:p-5 flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-brand-muted">Active Contributors</p>
                  <h3 className="text-xl sm:text-2xl font-black text-brand-ink mt-1">{stats.uniqueContributors}</h3>
                  <p className="text-[10px] text-brand-muted mt-0.5">Distinct actors</p>
                </div>
                <div className="p-2.5 rounded-xl bg-teal-100/60 dark:bg-teal-950/40 text-teal-600">
                  <User className="h-5 w-5" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filter & Search Toolbar */}
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 bg-surface p-3.5 rounded-xl border border-brand-border shadow-card">
            <div className="flex flex-1 flex-wrap items-center gap-2.5">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-brand-muted" />
                <Input
                  type="text"
                  placeholder="Search actions, entities, or users..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-9 h-9 text-xs"
                />
              </div>

              {/* Entity Type Filter */}
              <select
                value={entityFilter}
                onChange={(e) => {
                  setEntityFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="h-9 rounded-input border border-brand-border bg-surface px-3 text-xs text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand/30"
              >
                <option value="all">All Modules</option>
                <option value="task">Tasks</option>
                <option value="project">Projects</option>
                <option value="milestone">Milestones</option>
                <option value="client">Clients</option>
                <option value="lead">Leads</option>
                <option value="time_entry">Time Tracking</option>
                <option value="standup">Standups</option>
                <option value="branding">Branding</option>
                <option value="security">Security & MCP</option>
                {user?.role === 'owner' && <option value="finance">Finance</option>}
              </select>

              {/* Actor Filter */}
              <select
                value={actorFilter}
                onChange={(e) => {
                  setActorFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="h-9 rounded-input border border-brand-border bg-surface px-3 text-xs text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand/30"
              >
                <option value="all">All Team Members</option>
                {uniqueActors.map((actor) => (
                  <option key={actor.id} value={actor.id}>
                    {actor.name}
                  </option>
                ))}
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-1 border border-brand-border rounded-lg p-0.5 bg-brand-surface self-end md:self-auto shrink-0">
              <button
                onClick={() => setViewMode('timeline')}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                  viewMode === 'timeline'
                    ? 'bg-brand text-white shadow-xs'
                    : 'text-brand-muted hover:text-brand-ink'
                }`}
              >
                Timeline
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                  viewMode === 'table'
                    ? 'bg-brand text-white shadow-xs'
                    : 'text-brand-muted hover:text-brand-ink'
                }`}
              >
                Table
              </button>
            </div>
          </div>

          {/* Main Content Area: Skeleton vs Timeline vs Table */}
          {loading ? (
            viewMode === 'timeline' ? (
              <SkeletonTimeline items={6} />
            ) : (
              <SkeletonTable rows={8} cols={5} />
            )
          ) : filteredLogs.length === 0 ? (
            <Card className="text-center p-12 space-y-3">
              <Activity className="h-8 w-8 text-brand-muted mx-auto" />
              <h3 className="text-sm font-bold text-brand-ink">No activity records found</h3>
              <p className="text-xs text-brand-muted max-w-sm mx-auto">
                {search || entityFilter !== 'all' || actorFilter !== 'all'
                  ? 'Try adjusting your filters or search keywords to view past activities.'
                  : 'As your agency completes tasks, creates projects, and logs time, events will appear here in real time.'}
              </p>
            </Card>
          ) : viewMode === 'timeline' ? (
            /* Timeline View */
            <div className="rounded-2xl border border-brand-border bg-surface p-4 sm:p-6 shadow-card space-y-4">
              <div className="relative pl-6 sm:pl-8 space-y-6 before:absolute before:left-3 sm:before:left-4 before:top-2 before:bottom-2 before:w-0.5 before:bg-brand-border/60">
                {paginatedLogs.map((log) => {
                  const formattedTime = new Date(log.created_at).toLocaleString('en-US', {
                    timeZone: 'Asia/Karachi',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  });

                  return (
                    <div key={log.id} className="relative group">
                      {/* Timeline dot */}
                      <div className="absolute -left-6 sm:-left-8 top-1.5 h-6 w-6 rounded-full border-2 border-surface bg-brand-tint flex items-center justify-center text-brand shadow-xs">
                        <div className="h-2 w-2 rounded-full bg-brand" />
                      </div>

                      {/* Event Card */}
                      <div className="rounded-xl border border-brand-border bg-surface p-4 hover:border-brand/40 hover:shadow-hover transition-all space-y-2.5">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
                          <div className="flex items-center space-x-2.5">
                            <div className="h-6 w-6 rounded-full bg-brand-tint flex items-center justify-center text-brand-ink font-bold text-[10px] shrink-0">
                              {log.actor_name ? log.actor_name.charAt(0).toUpperCase() : 'U'}
                            </div>
                            <span className="text-xs font-bold text-brand-ink">{log.actor_name}</span>
                            {getActionBadge(log.action)}
                            <span className="text-[11px] font-semibold text-brand-muted uppercase tracking-wider hidden md:inline">
                              • {log.entity_type}
                            </span>
                          </div>
                          <span className="text-[11px] text-brand-muted flex items-center gap-1 font-mono">
                            <Clock className="h-3 w-3" />
                            {formattedTime} (PKT)
                          </span>
                        </div>

                        <p className="text-xs text-brand-ink leading-relaxed font-medium">
                          {log.description}
                        </p>

                        {log.entity_name && (
                          <div className="text-[11px] text-brand-muted flex items-center gap-1.5 pt-1">
                            <FileText className="h-3 w-3 text-brand" />
                            <span>Target:</span>
                            <span className="font-semibold text-brand-ink">{log.entity_name}</span>
                          </div>
                        )}

                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                          <div className="pt-2 border-t border-brand-border/40 flex items-center justify-between">
                            <span className="text-[10px] text-brand-muted font-mono">
                              Payload: {Object.keys(log.metadata).length} attributes
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedLog(log)}
                              className="h-6 px-2 text-[11px] text-brand hover:text-brand-hover hover:bg-brand-tint/30"
                            >
                              <Eye className="h-3 w-3 mr-1" /> View Details
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <Pagination
                currentPage={validCurrentPage}
                totalItems={filteredLogs.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
              />
            </div>
          ) : (
            /* Detailed Table View */
            <div className="space-y-4">
              <div className="rounded-xl border border-brand-border bg-surface overflow-hidden shadow-card">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-brand-border bg-slate-50/70 dark:bg-[#0E1A18] text-brand-muted font-bold uppercase tracking-wider text-[10px]">
                        <th className="py-3 px-4">Timestamp (PKT)</th>
                        <th className="py-3 px-4">Actor</th>
                        <th className="py-3 px-4">Action</th>
                        <th className="py-3 px-4">Module</th>
                        <th className="py-3 px-4">Description</th>
                        <th className="py-3 px-4 text-right">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-border/50 text-brand-ink">
                      {paginatedLogs.map((log) => {
                        const formattedTime = new Date(log.created_at).toLocaleString('en-US', {
                          timeZone: 'Asia/Karachi',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        });

                        return (
                          <tr key={log.id} className="hover:bg-slate-50/50 dark:hover:bg-[#162C29]/30 transition-colors">
                            <td className="py-3 px-4 font-mono text-[11px] text-brand-muted whitespace-nowrap">
                              {formattedTime}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap font-semibold">
                              <div className="flex items-center space-x-1.5">
                                <div className="h-5 w-5 rounded-full bg-brand-tint flex items-center justify-center text-brand-ink text-[10px]">
                                  {log.actor_name ? log.actor_name.charAt(0) : 'U'}
                                </div>
                                <span>{log.actor_name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap">{getActionBadge(log.action)}</td>
                            <td className="py-3 px-4 whitespace-nowrap uppercase tracking-wider text-[10px] font-bold text-brand-muted">
                              {log.entity_type}
                            </td>
                            <td className="py-3 px-4 max-w-md truncate font-medium">
                              {log.description}
                            </td>
                            <td className="py-3 px-4 text-right whitespace-nowrap">
                              {log.metadata && Object.keys(log.metadata).length > 0 ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setSelectedLog(log)}
                                  className="h-6 px-2 text-[11px] text-brand hover:bg-brand-tint/30"
                                >
                                  <Eye className="h-3 w-3 mr-1" /> Inspect
                                </Button>
                              ) : (
                                <span className="text-brand-muted text-[11px]">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <Pagination
                currentPage={validCurrentPage}
                totalItems={filteredLogs.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      {/* Audit Detail Inspector Modal */}
      {selectedLog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in"
          onClick={() => setSelectedLog(null)}
        >
          <div
            className="w-full max-w-lg rounded-2xl border border-brand-border bg-surface shadow-2xl overflow-hidden text-brand-ink animate-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-brand-border px-6 py-4 bg-slate-50/70 dark:bg-[#0E1A18]">
              <div className="flex items-center space-x-2.5">
                <div className="p-2 rounded-xl bg-brand-tint/60 text-brand">
                  <Activity className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-brand-ink">Audit Event Payload</h3>
                  <p className="text-[11px] text-brand-muted font-mono">ID: {selectedLog.id}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-1 rounded-lg text-brand-muted hover:text-brand-ink hover:bg-brand-tint/40 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[65vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-2.5 rounded-lg border border-brand-border/60 bg-brand-surface">
                  <span className="text-[10px] uppercase font-bold text-brand-muted">Actor</span>
                  <p className="font-semibold text-brand-ink mt-0.5">{selectedLog.actor_name}</p>
                </div>
                <div className="p-2.5 rounded-lg border border-brand-border/60 bg-brand-surface">
                  <span className="text-[10px] uppercase font-bold text-brand-muted">Action</span>
                  <p className="font-semibold text-brand-ink mt-0.5">{selectedLog.action}</p>
                </div>
                <div className="p-2.5 rounded-lg border border-brand-border/60 bg-brand-surface">
                  <span className="text-[10px] uppercase font-bold text-brand-muted">Entity Type</span>
                  <p className="font-semibold text-brand-ink mt-0.5 uppercase">{selectedLog.entity_type}</p>
                </div>
                <div className="p-2.5 rounded-lg border border-brand-border/60 bg-brand-surface">
                  <span className="text-[10px] uppercase font-bold text-brand-muted">Timestamp (PKT)</span>
                  <p className="font-semibold text-brand-ink mt-0.5 font-mono text-[11px]">
                    {new Date(selectedLog.created_at).toLocaleString('en-US', { timeZone: 'Asia/Karachi' })}
                  </p>
                </div>
              </div>

              <div>
                <span className="text-xs font-bold text-brand-muted uppercase tracking-wider">Description</span>
                <p className="text-xs text-brand-ink font-medium mt-1 p-3 rounded-lg border border-brand-border/60 bg-surface">
                  {selectedLog.description}
                </p>
              </div>

              {selectedLog.metadata && (
                <div>
                  <span className="text-xs font-bold text-brand-muted uppercase tracking-wider">
                    Context Metadata (Sanitized)
                  </span>
                  <pre className="mt-1 p-3 rounded-lg bg-slate-900 text-emerald-400 font-mono text-[11px] overflow-x-auto max-h-48 border border-slate-800">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            <div className="flex justify-end border-t border-brand-border px-6 py-3 bg-slate-50/50 dark:bg-[#0E1A18]">
              <Button size="sm" variant="outline" onClick={() => setSelectedLog(null)} className="text-xs">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActivityLogsPage;
